/**
 * Tâche 1216 — Carte « Veille disque » (Eric 2026-06-26).
 * Tâche 1242 — (Eric 2026-06-28) Le disque qui a saturé n'était pas surveillé.
 *
 * 1216 : les bogues 1213 et 1215 avaient la même cause — /tmp saturé, sans
 * aucun capteur. 1242 : le 2026-06-28, c'est le volume de `/app` qui est
 * arrivé à 100 % (dépôt git, 1 589 archives de sauvegarde, node_modules).
 * Pytest ne pouvait plus écrire et une sauvegarde a été tronquée en pleine
 * écriture — personne n'a été prévenu. La carte montre donc CHAQUE volume,
 * dit QUI occupe la place, annonce la saturation AVANT qu'elle arrive, et
 * offre la purge des vieilles archives en un clic.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { HardDrives, Broom, ArrowsClockwise, Archive, TrendDown } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

const levelOf = (v) =>
  v.used_pct >= v.critical_pct ? "critical" : v.used_pct >= v.warn_pct ? "warn" : "ok";

const TONES = {
  ok: "border-emerald-200 bg-emerald-50 text-emerald-900",
  warn: "border-amber-300 bg-amber-50 text-amber-900",
  critical: "border-red-300 bg-red-50 text-red-900",
};
const BARS = { ok: "bg-emerald-500", warn: "bg-amber-500", critical: "bg-red-600" };

const VolumeRow = ({ vol }) => {
  const level = levelOf(vol);
  return (
    <div className="mt-3" data-testid={`disk-volume-${vol.key}`}>
      <div className="flex items-baseline justify-between gap-2 text-xs">
        <span className="font-semibold text-zinc-800">{vol.label}</span>
        <span className="text-zinc-600">
          {vol.used_pct} % · {vol.free_gb} Go libres sur {vol.total_gb} Go
        </span>
      </div>
      <div className="mt-1 h-2 w-full rounded-full bg-zinc-100 overflow-hidden">
        <div
          className={`h-full ${BARS[level]}`}
          style={{ width: `${Math.min(100, vol.used_pct)}%` }}
        />
      </div>
      {vol.forecast && (
        <p
          className={`mt-1 text-xs ${
            vol.forecast.hours_left <= 12 ? "text-red-700 font-semibold" : "text-amber-700"
          }`}
          data-testid={`disk-forecast-${vol.key}`}
        >
          <TrendDown size={12} className="inline mr-1" />
          Plein dans environ {vol.forecast.hours_left} h au rythme actuel (
          {vol.forecast.rate_gb_per_hour} Go/h, mesuré sur{" "}
          {vol.forecast.based_on_minutes} min).
        </p>
      )}
    </div>
  );
};

export const DiskSentinelCard = ({ initial , awaiting }) => {
  const [disk, setDisk] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [hidden, setHidden] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/sentinel/disk/status`);
      setDisk(data);
    } catch (e) {
      if (e?.response?.status === 403) setHidden(true);
    }
  }, []);

    // Tâche 1249 — Si la page a déjà tout reçu dans l'appel groupé, la carte
  // ne refait PAS son propre appel : la page passait 23 appels par affichage.
  useEffect(() => {
    if (initial) {
      setDisk(initial);
      return;
    }
    // Tâche 1249 — L'appel groupé est en route : ne pas en lancer un second.
    if (awaiting) return;
    load();
  }, [load, initial, awaiting]);

  const purge = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/sentinel/disk/purge-cache`);
      setDisk(data.disk);
      toast.success(
        `Cache vidéo purgé : ${data.cache.files_removed} fichier(s), ${data.cache.freed_gb} Go libérés`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purge impossible");
    } finally {
      setBusy(false);
    }
  };

  const purgeArchives = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/sentinel/disk/purge-backups`);
      setDisk(data.disk);
      const b = data.backups;
      toast.success(
        b.removed
          ? `${b.removed} archive(s) effacée(s) sur ${b.orgs} organisation(s), ${b.freed_mb} Mo libérés`
          : `Rien à purger : chaque organisation a au plus ${b.retention} archives locales`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purge des archives impossible");
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  };

  if (hidden || !disk) return null;

  const pct = disk.used_pct;
  const level = pct >= disk.critical_pct ? "critical" : pct >= disk.warn_pct ? "warn" : "ok";
  const vols = disk.volumes || [];
  const worst = vols.reduce(
    (acc, v) => (acc && acc.used_pct >= v.used_pct ? acc : v),
    null
  );
  const worstLevel = worst ? levelOf(worst) : level;
  const rows = (disk.breakdown || []).filter((r) => r.gb !== 0);

  return (
    <section
      className="rounded-lg border border-zinc-200 bg-white p-5"
      data-testid="disk-sentinel-card"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <HardDrives size={18} weight="bold" className="text-zinc-700" />
          <h2 className="font-semibold text-sm">Veille disque</h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={load}
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded border border-zinc-300 bg-white hover:bg-zinc-50"
            data-testid="disk-refresh-btn"
          >
            <ArrowsClockwise size={13} /> Revérifier
          </button>
          <button
            onClick={purge}
            disabled={busy}
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded border border-zinc-300 bg-white hover:bg-zinc-50 disabled:opacity-50"
            data-testid="disk-purge-btn"
            title="Vide le cache vidéo : les vidéos seront simplement re-téléchargées à la demande"
          >
            <Broom size={13} /> Purger le cache vidéo
          </button>
          {confirming ? (
            <div className="flex items-center gap-2">
              <button
                onClick={purgeArchives}
                disabled={busy}
                className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                data-testid="disk-purge-backups-confirm-btn"
              >
                <Archive size={13} /> Confirmer la purge
              </button>
              <button
                onClick={() => setConfirming(false)}
                className="text-xs font-semibold px-2 py-1.5 rounded border border-zinc-300 bg-white hover:bg-zinc-50"
                data-testid="disk-purge-backups-cancel-btn"
              >
                Annuler
              </button>
            </div>
          ) : (
            <button
              onClick={() => setConfirming(true)}
              disabled={busy}
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded bg-zinc-900 text-white hover:bg-zinc-800 disabled:opacity-50"
              data-testid="disk-purge-backups-btn"
              title="Efface les archives locales au-delà des plus récentes de chaque organisation"
            >
              <Archive size={13} /> Purger les vieilles archives
            </button>
          )}
        </div>
      </div>

      {confirming && (
        <p
          className="mt-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
          data-testid="disk-purge-backups-warning"
        >
          Les 10 archives les plus récentes de chaque organisation sont conservées, ainsi
          que tout l'historique et les copies OVH. Seuls les fichiers locaux plus anciens
          sont effacés.
        </p>
      )}

      <div
        className={`mt-3 rounded-md border px-3 py-2 text-xs ${TONES[worstLevel]}`}
        data-testid="disk-verdict"
      >
        <strong>
          {worst ? `${worst.label} : ${worst.used_pct} % occupé` : `${pct} % occupé`}
        </strong>{" "}
        — {worst ? worst.free_gb : disk.free_gb} Go libres sur{" "}
        {worst ? worst.total_gb : disk.total_gb} Go.{" "}
        {worstLevel === "critical"
          ? "Saturation imminente : plus rien ne peut être écrit (sauvegarde tronquée, transcriptions en échec). Purgez, ou augmentez le tier de déploiement."
          : worstLevel === "warn"
            ? `Au-delà de ${disk.critical_pct} %, les écritures commencent à échouer.`
            : "Marge confortable pour les transcriptions et les sauvegardes."}
      </div>

      {vols.map((vol) => (
        <VolumeRow key={vol.key} vol={vol} />
      ))}

      {rows.length > 0 && (
        <div className="mt-4" data-testid="disk-breakdown">
          <div className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
            Qui occupe la place
          </div>
          <ul className="mt-1 space-y-1">
            {rows.map((r) => (
              <li
                key={r.path}
                className="flex items-baseline justify-between gap-2 text-xs text-zinc-700"
                data-testid={`disk-breakdown-row-${r.label}`}
              >
                <span>{r.label}</span>
                <span className="font-mono text-zinc-600">
                  {r.gb < 0 ? "inconnu" : `${r.gb} Go`}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <p className="mt-3 text-xs text-zinc-600" data-testid="disk-cache-line">
        Cache vidéo : {disk.cache_gb} Go ({disk.cache_files} fichier(s)). Purgé
        automatiquement à chaque déploiement, et dès {disk.critical_pct} % d'occupation.
        Une alerte part dès qu'un volume doit être plein dans moins de{" "}
        {disk.forecast_alert_hours || 12} h.
      </p>
    </section>
  );
};

export default DiskSentinelCard;
